import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, CircularProgress, ToggleButton, ToggleButtonGroup, MenuItem, Stack,
} from '@mui/material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const sessions = (n) => (n == null ? '—' : `${Number(n)} session${Number(n) === 1 ? '' : 's'}`);

const LEVELS = [
  { value: 'all', label: 'All levels' },
  { value: 'enterprise', label: 'Enterprises' },
  { value: 'organization', label: 'Organisations' },
  { value: 'region', label: 'Regions' },
  { value: 'clinic', label: 'Offices' },
];
const LEVEL_LABEL = { enterprise: 'Enterprise', organization: 'Organisation', region: 'Region', clinic: 'Office' };
const LEVEL_COLOR = { enterprise: '#6d28d9', organization: '#0f766e', region: '#b45309', clinic: '#334155' };
// The id field /superadmin/credits/grant expects for each level.
const ID_FIELD = { enterprise: 'enterprise_id', organization: 'organization_id', region: 'region_id', clinic: 'clinic_id' };

/**
 * The customer tree as table rows, parents before children, each carrying its depth
 * and the customer (tree root) it belongs to — which is what the Customer filter keys on.
 * A pool's cost is what its offices spent, so it is summed on the way back up.
 */
function flatten(nodes, clinicsById, depth = 0, root = null, out = []) {
  nodes.forEach((n) => {
    const key = `${n.scope}:${n.id}`;
    const row = { ...n, key, depth, root: root || key, cost: 0 };
    if (n.scope === 'clinic') {
      const c = clinicsById[n.id] || {};
      row.provider_count = c.provider_count;
      row.pending_requests = c.pending_requests || 0;
      row.cost = Number(c.used_usd || 0);
    }
    out.push(row);
    const start = out.length;
    flatten(n.children || [], clinicsById, depth + 1, root || key, out);
    if (n.scope !== 'clinic') {
      row.cost = out.slice(start).filter((r) => r.scope === 'clinic').reduce((t, r) => t + r.cost, 0);
    }
  });
  return out;
}

export default function Credits() {
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [grant, setGrant] = useState(null);
  const [granting, setGranting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState('all');
  const [level, setLevel] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [c, r, m, t] = await Promise.all([
        axios.get(`${API_URL}/api/v1/superadmin/credits`),
        axios.get(`${API_URL}/api/v1/superadmin/credit-requests?status=pending`),
        axios.get(`${API_URL}/api/v1/superadmin/credits/monthly?months=12`).catch(() => null),
        axios.get(`${API_URL}/api/v1/superadmin/customers`).catch(() => null),
      ]);
      setData(c.data);
      setCustomers(t?.data?.customers || []);
      setRequests(r.data.requests || []);
      setMonthly(m?.data?.months || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep link from the clinic page (/credits?clinic=<id>) → open that clinic's grant dialog.
  useEffect(() => {
    const cid = searchParams.get('clinic');
    if (!cid || !data?.clinics) return;
    const c = data.clinics.find((x) => x.clinic_id === cid);
    if (c) {
      setGrant({ scope: 'clinic', id: c.clinic_id, name: c.name, unlimited: !!c.unlimited, current: c.granted,
        mode: 'add', amount: '', left: c.remaining, committed: c.used || 0 });
    } else {
      toast.error('This clinic has no AI-session record yet.');
    }
    searchParams.delete('clinic');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const rows = useMemo(() => {
    const byId = Object.fromEntries((data?.clinics || []).map((c) => [c.clinic_id, c]));
    const all = flatten(customers, byId);
    // Offices that belong to no customer tree at all (no organisation) still get a row.
    const seen = new Set(all.filter((r) => r.scope === 'clinic').map((r) => r.id));
    (data?.clinics || []).filter((c) => !seen.has(c.clinic_id)).forEach((c) => {
      all.push({
        scope: 'clinic', id: c.clinic_id, key: `clinic:${c.clinic_id}`, name: c.name, depth: 0,
        root: `clinic:${c.clinic_id}`, is_active: true, credits: c, provider_count: c.provider_count,
        pending_requests: c.pending_requests || 0, cost: Number(c.used_usd || 0),
      });
    });
    return all;
  }, [customers, data]);

  const customerOptions = useMemo(
    () => rows.filter((r) => r.depth === 0 && r.scope !== 'clinic'),
    [rows],
  );
  const visible = rows.filter((r) => (customer === 'all' || r.root === customer)
    && (level === 'all' || r.scope === level));
  // Indent only when showing the tree; a single level reads better as a flat list.
  const indent = level === 'all';

  const openGrant = (r) => {
    const cr = r.credits || {};
    const isOffice = r.scope === 'clinic';
    // Everything beneath this row, which "Reset to zero" empties along with it: the rows
    // after it in the tree that sit deeper, up to the next row at its own depth or above.
    const at = rows.findIndex((x) => x.key === r.key);
    const below = [];
    for (let i = at + 1; i < rows.length && rows[i].depth > r.depth; i += 1) below.push(rows[i]);
    const count = (sc) => below.filter((x) => x.scope === sc).length;
    setGrant({ scope: r.scope, id: r.id, name: r.name, unlimited: !!cr.unlimited, current: cr.granted,
      allocated: cr.allocated, mode: 'add', amount: '',
      // What "Reset to zero" would take away, and what it has to leave behind.
      left: isOffice ? cr.remaining : cr.available,
      committed: (isOffice ? cr.used : cr.allocated) || 0,
      beneath: { organization: count('organization'), region: count('region'), clinic: count('clinic') },
      hasParent: r.depth > 0 });
  };

  const openDetail = async (clinic) => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/credits/clinic/${clinic.clinic_id}`);
      setDetail(res.data);
    } catch (e) { toast.error('Failed to load breakdown'); }
  };

  const submitGrant = async () => {
    const g = grant;
    const amount = g.unlimited ? null : parseInt(g.amount, 10);
    // An exact limit of 0 empties this level and everything beneath it — the same thing
    // as "Reset to zero", so it goes through the same confirmation.
    if (!g.unlimited && g.mode === 'set' && amount === 0) { setGrant({ ...g, confirmReset: true }); return; }
    if (!g.unlimited && (!amount || amount <= 0)) { toast.error('Enter a number of sessions (or pick Unlimited)'); return; }
    if (granting) return;
    setGranting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/superadmin/credits/grant`, {
        scope: g.scope, [ID_FIELD[g.scope]]: g.id, unlimited: g.unlimited, amount_sessions: amount, mode: g.mode || 'add',
      });
      // Inside a customer's hierarchy the change moves sessions to or from a pool above;
      // say which, so the operator knows the customer's own figures moved too.
      toast.success(data?.message ? `Sessions updated. ${data.message}` : 'Sessions updated',
        { duration: data?.message ? 6000 : 3000 });
      setGrant(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to grant'); }
    finally { setGranting(false); }
  };

  const resetToZero = async () => {
    const g = grant;
    if (granting) return;
    setGranting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/superadmin/credits/grant`, {
        scope: g.scope, [ID_FIELD[g.scope]]: g.id, mode: 'reset',
      });
      toast.success(data?.message || 'Reset to zero', { duration: 6000 });
      setGrant(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reset'); }
    finally { setGranting(false); }
  };

  const review = async (id, action, amountSessions) => {
    let amount = amountSessions;
    if (action === 'approve' && (amount == null || amount <= 0)) {
      const entered = window.prompt('How many sessions to grant this clinic?');
      if (entered == null) return;
      amount = parseInt(entered, 10);
      if (!amount || amount <= 0) { toast.error('Enter a valid number of sessions'); return; }
    }
    try {
      await axios.put(`${API_URL}/api/v1/superadmin/credit-requests/${id}`, { action, amount_sessions: amount });
      toast.success(action === 'approve' ? `Approved — granted ${sessions(amount)}` : 'Request declined');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Fraunces", Georgia, serif', mb: 0.5 }}>AI Credits</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Grant AI sessions to any level, or set it Unlimited. An enterprise, organisation or region holds a
        <b> pool</b> its admins hand down; an office spends sessions as providers generate notes (one per note),
        and once it runs out AI note generation is blocked there until more are added.
        The <b>Cost</b> column shows the estimated Gemini spend — visible to platform admins only.
      </Typography>

      {requests.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #f59e0b66', borderRadius: 3, bgcolor: '#fffbeb' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Pending session requests ({requests.length})</Typography>
          {requests.map((r) => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: '1px solid #00000010', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography fontWeight={600}>{r.clinic_name} — requests {r.amount_sessions != null ? sessions(r.amount_sessions) : 'sessions (amount unspecified)'}</Typography>
                <Typography variant="caption" color="text.secondary">{r.requested_by}{r.note ? ` · "${r.note}"` : ''}</Typography>
              </Box>
              <Button size="small" variant="contained" color="success" onClick={() => review(r.id, 'approve', r.amount_sessions)}>
                {r.amount_sessions != null ? `Approve ${r.amount_sessions}` : 'Approve'}
              </Button>
              <Button size="small" color="error" onClick={() => review(r.id, 'decline')}>Decline</Button>
            </Box>
          ))}
        </Paper>
      )}

      {/* Month by month, platform-wide. A "credit" here is the same unit the grants
          use — one distinct session that consumed AI — so these rows reconcile with
          the Used column below. */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Usage by month</Typography>
          <Typography variant="caption" color="text.secondary">
            Last 12 months across every clinic. Costs are estimates (tokens × configured rates);
            Google billing is authoritative.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Credits used</TableCell>
                <TableCell align="right">Clinics active</TableCell>
                <TableCell align="right">AI calls</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Est. cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthly.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No AI usage recorded yet.
                  </TableCell>
                </TableRow>
              ) : monthly.map((m, i) => (
                <TableRow key={m.month} hover sx={i === 0 ? { bgcolor: 'rgba(201,162,39,0.06)' } : undefined}>
                  <TableCell sx={{ fontWeight: i === 0 ? 700 : 400 }}>
                    {m.label}
                    {i === 0 && <Chip size="small" label="This month" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">{Number(m.credits_used).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(m.clinics_active).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(m.calls).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(m.tokens).toLocaleString()}</TableCell>
                  <TableCell align="right">{money(m.cost)}</TableCell>
                </TableRow>
              ))}
              {monthly.length > 1 && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Total ({monthly.length} months)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {monthly.reduce((n, m) => n + m.credits_used, 0).toLocaleString()}
                  </TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {monthly.reduce((n, m) => n + m.calls, 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {monthly.reduce((n, m) => n + m.tokens, 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {money(monthly.reduce((n, m) => n + m.cost, 0))}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Customer" value={customer}
          onChange={(e) => setCustomer(e.target.value)} sx={{ minWidth: 280 }}>
          <MenuItem value="all">All customers</MenuItem>
          {customerOptions.map((r) => (
            <MenuItem key={r.key} value={r.key}>
              {r.name}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {LEVEL_LABEL[r.scope]}
              </Typography>
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={level} onChange={(e, v) => { if (v) setLevel(v); }}
          sx={{ flexWrap: 'wrap' }}>
          {LEVELS.map((l) => <ToggleButton key={l.value} value={l.value}>{l.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {visible.length} row{visible.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Level</TableCell>
                <TableCell align="right">Providers</TableCell>
                <TableCell>Limit</TableCell>
                <TableCell align="right">Used / passed on</TableCell>
                <TableCell align="right">Remaining / to give</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Nothing at this level for this customer.
                  </TableCell>
                </TableRow>
              )}
              {visible.map((r) => {
                const cr = r.credits || {};
                const isOffice = r.scope === 'clinic';
                const committed = isOffice ? cr.used : cr.allocated;
                const left = isOffice ? cr.remaining : cr.available;
                const out = !cr.unlimited && left != null && left <= 0;
                return (
                  <TableRow key={r.key} hover sx={r.depth === 0 && !isOffice ? { bgcolor: 'rgba(201,162,39,0.05)' } : undefined}>
                    <TableCell sx={{ pl: 2 + (indent ? r.depth * 3 : 0) }}>
                      <Box component="span" sx={{ fontWeight: isOffice ? 400 : 700 }}>{r.name}</Box>
                      {r.is_active === false && <Chip size="small" label="Closed" sx={{ ml: 1 }} />}
                      {r.pending_requests > 0 && <Chip size="small" color="warning" label={`${r.pending_requests} req`} sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined"
                        label={r.is_region_practice ? 'Region practice' : LEVEL_LABEL[r.scope]}
                        sx={{ color: LEVEL_COLOR[r.scope], borderColor: LEVEL_COLOR[r.scope] }} />
                    </TableCell>
                    <TableCell align="right">{isOffice ? (r.provider_count ?? '—') : '—'}</TableCell>
                    <TableCell>
                      {cr.unlimited
                        ? <Chip size="small" color="success" label={cr.has_account === false ? 'Unlimited (default)' : 'Unlimited'} />
                        : sessions(cr.granted)}
                    </TableCell>
                    <TableCell align="right">{sessions(committed ?? 0)}</TableCell>
                    <TableCell align="right" sx={{ color: out ? 'error.main' : 'inherit', fontWeight: out ? 700 : 400 }}>
                      {cr.unlimited ? '—' : sessions(left)}
                    </TableCell>
                    <TableCell align="right">{money(r.cost)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {isOffice && <Button size="small" onClick={() => openDetail({ clinic_id: r.id })}>Breakdown</Button>}
                      <Button size="small" variant="contained" onClick={() => openGrant(r)}
                        sx={{ ml: 1, bgcolor: '#c9a227', color: '#0f172a', '&:hover': { bgcolor: '#b8941f' } }}>Edit / Grant</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!grant} onClose={() => { if (!granting) setGrant(null); }} fullWidth maxWidth="xs">
        <DialogTitle>AI sessions — {grant?.name}</DialogTitle>
        <DialogContent>
          {grant && grant.scope !== 'clinic' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This is the {LEVEL_LABEL[grant.scope].toLowerCase()}'s pool. Its admins hand these sessions
              down to what sits beneath it{grant.allocated ? ` — ${grant.allocated} already passed on` : ''}.
            </Typography>
          )}
          <FormControlLabel sx={{ mt: 1 }}
            control={<Switch checked={!!grant?.unlimited} onChange={(e) => setGrant({ ...grant, unlimited: e.target.checked })} />}
            label="Unlimited" />
          {!grant?.unlimited && (
            <>
              <ToggleButtonGroup size="small" exclusive fullWidth sx={{ mt: 1, mb: 2 }}
                value={grant?.mode || 'add'}
                onChange={(e, v) => { if (v) setGrant({ ...grant, mode: v, amount: v === 'set' ? String(grant?.current ?? '') : '' }); }}>
                <ToggleButton value="add">Add sessions</ToggleButton>
                <ToggleButton value="set">Set exact limit</ToggleButton>
              </ToggleButtonGroup>
              <TextField fullWidth type="number"
                label={grant?.mode === 'set' ? 'New total limit (sessions)' : 'Add sessions'}
                value={grant?.amount || ''}
                onChange={(e) => setGrant({ ...grant, amount: e.target.value })} inputProps={{ min: 0, step: '1' }}
                helperText={grant?.mode === 'set'
                  ? `Replaces the current limit (${grant?.current != null ? grant.current + ' sessions' : '—'}).`
                    + (grant?.scope !== 'clinic' ? ' 0 empties everything beneath it too.' : '')
                  : (grant?.scope === 'clinic' ? "Adds to the office's existing allowance." : 'Adds to the pool.')} />
            </>
          )}
        </DialogContent>
        {grant?.confirmReset && (
          <Box sx={{ mx: 3, mb: 1, p: 1.75, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
              Reset {grant.name} to zero?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {grant.scope === 'clinic'
                ? `It will have nothing left to use${grant.committed > 0 ? ` — the ${grant.committed} already used can't be taken back` : ''}.`
                : (() => {
                  const parts = [
                    ['organization', 'organisation'], ['region', 'region'], ['clinic', 'office'],
                  ].map(([k, w]) => (grant.beneath?.[k] ? `${grant.beneath[k]} ${w}${grant.beneath[k] === 1 ? '' : 's'}` : null))
                    .filter(Boolean);
                  return `This empties it and everything beneath it${parts.length ? ` — ${parts.join(', ')}` : ''}. `
                    + 'Every pool is left with nothing to give, and every office with nothing to use; '
                    + 'offices keep only the sessions they have already used.';
                })()}
              {' '}{grant.hasParent
                ? 'Unspent sessions go back to the level above.'
                : 'Unspent sessions are removed from this customer.'}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => setGrant({ ...grant, confirmReset: false })} disabled={granting}>
                Keep sessions
              </Button>
              <Button size="small" variant="contained" color="error" onClick={resetToZero} disabled={granting}>
                {granting ? 'Resetting…' : 'Yes, reset to zero'}
              </Button>
            </Stack>
          </Box>
        )}
        <DialogActions>
          {/* A pool always offers it (something beneath may still hold sessions); an
              office only when it has something left to remove. */}
          {grant && !grant.confirmReset && (grant.scope !== 'clinic' || grant.unlimited || (grant.left ?? 0) > 0) && (
            <Button color="error" onClick={() => setGrant({ ...grant, confirmReset: true })}
              disabled={granting} sx={{ mr: 'auto' }}>
              Reset to zero
            </Button>
          )}
          <Button onClick={() => setGrant(null)} disabled={granting}>Cancel</Button>
          <Button variant="contained" onClick={submitGrant} disabled={granting || grant?.confirmReset}
            startIcon={granting ? <CircularProgress size={16} color="inherit" /> : null}>
            {granting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>{detail?.clinic_name} — sessions breakdown</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Box sx={{ mb: 2 }}>
                {detail.unlimited
                  ? <Chip size="small" color="success" label="Unlimited" />
                  : <Typography variant="body2">Granted <b>{sessions(detail.granted)}</b> · Used {sessions(detail.used)} · Remaining <b>{sessions(detail.remaining)}</b></Typography>}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Estimated Gemini cost: <b>{money(detail.used_usd)}</b> (platform admins only)
                </Typography>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Per provider</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Provider</TableCell><TableCell align="right">Sessions</TableCell><TableCell align="right">Calls</TableCell><TableCell align="right">Cost</TableCell></TableRow></TableHead>
                <TableBody>
                  {detail.providers.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 2 }}>No AI usage yet</TableCell></TableRow>}
                  {detail.providers.map((p, i) => (
                    <TableRow key={i}><TableCell>{p.name}</TableCell><TableCell align="right">{p.sessions}</TableCell><TableCell align="right">{p.calls}</TableCell><TableCell align="right">{money(p.used)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Grant history</Typography>
              {detail.grant_history.length === 0 && <Typography variant="caption" color="text.secondary">No grants yet.</Typography>}
              {detail.grant_history.map((g, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">
                  {(g.created_at || '').slice(0, 10)} — {g.action === 'set_unlimited'
                    ? 'set Unlimited'
                    : g.amount_sessions != null
                      ? `${g.amount_sessions < 0 ? '−' : '+'}${sessions(Math.abs(g.amount_sessions))}`
                      : `+${money(g.amount_usd)}`}{g.note ? ` (${g.note})` : ''}
                </Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
